import React, { useEffect, useMemo, useContext, useRef, useCallback } from 'react'
import { connect } from 'react-redux'
import GeoContext from 'contexts/geoContext'
import clsx from 'clsx'
import styled from 'styled-components'
import { fetchCruisePromoBanners } from 'actions/CruiseActions'
import { useAppDispatch } from 'hooks/reduxHooks'
import CardCarousel from 'components/Luxkit/Carousel/CardCarousel'
import ImpressionTrackerContext from 'contexts/ImpressionTrackerContext'
import { GAImpressionFieldObject } from 'analytics/eventDefinitions'
import uuidV4 from 'lib/string/uuidV4Utils'
import { getListName, productClick, productImpression } from 'analytics/enhanced-ecommerce'
import CruisePromoTileItem from './CruisePromoTileItem'

const CruisePromoTilesWrapper = styled.div`
  &.fixed-banner {
    display: grid;
    grid-template-columns: 1fr 2fr;
  }
`

const PromoTilesCarousel = styled(CardCarousel)`
  &.centralize-items {
    .paged {
      justify-content: center;
      grid-template-columns: unset;
    }
  }

  .paged {
    align-items: center;
  }
`

interface Props {
  cruisePromoBanners: Array<App.CruisePromoBanner>
  cruiseLineId?: string
  pageSize?: number
}

function CruisePromoTiles({ cruisePromoBanners, cruiseLineId, pageSize } : Props) {
  const dispatch = useAppDispatch()
  const { currentCurrency } = useContext(GeoContext)

  // fetch cruise banners
  useEffect(() => {
    dispatch(fetchCruisePromoBanners())
  }, [dispatch])

  // filter out expired banners
  const banners = useMemo(() => {
    const today = new Date()

    return cruisePromoBanners.filter(banner => {
      const cruiseLinePageValidation = cruiseLineId ? banner.isVisibleOnVendorPage && banner.vendorId === cruiseLineId : !banner.isVisibleOnVendorPage
      const startDate = new Date(banner.startDate)
      const endDate = new Date(banner.endDate)
      return (
        startDate <= today &&
        endDate >= today &&
        cruiseLinePageValidation
      )
    })
  }, [cruisePromoBanners, cruiseLineId])

  const fixedBanner = useMemo(() => banners.find((banner) => banner.isFixed), [banners])
  const showFixedBanner = fixedBanner && banners.length > 3
  const carouselBanners = useMemo(() => banners.filter((banner) => {
    if (showFixedBanner && banner.isFixed) {
      return false
    }

    return true
  }), [banners, showFixedBanner])

  const listId = useRef<string>(uuidV4())
  const listName = getListName(
    'CruisePromo',
    'Cruise',
    'Cruise promo tiles',
  )

  const impressionEventHandler = useCallback((id: string) => {
    const banner = carouselBanners.find((b) => b.id === id)

    if (banner) {
      const position = carouselBanners.findIndex((b) => b.id === id) + 1

      const impression: GAImpressionFieldObject = {
        id: banner.id,
        name: banner.description,
        list: listName,
        position,
        quantity: 1,
      }

      productImpression(currentCurrency, [impression], {
        listId: listId.current,
        listName,
      })
    }
  }, [currentCurrency, carouselBanners, listName])

  const clickEventHandler = useCallback((id: string) => {
    const banner = carouselBanners.find((b) => b.id === id)

    if (banner) {
      const position = carouselBanners.findIndex((b) => b.id === id) + 1

      productClick(
        currentCurrency,
        {
          id: banner.id,
          name: banner.description,
          position,
          quantity: 1,
        },
        {
          listId: listId.current,
          listName,
        },
      )
    }
  }, [currentCurrency, carouselBanners, listName])

  if (!banners.length) return null

  return <ImpressionTrackerContext.Provider value={impressionEventHandler}>
    <CruisePromoTilesWrapper
      className={clsx({
        'fixed-banner': showFixedBanner,
      })}
    >
      {showFixedBanner && <CruisePromoTileItem
        promoBanner={fixedBanner}
        onClick={clickEventHandler}
      />}

      <PromoTilesCarousel
        pageSize={pageSize ?? (showFixedBanner ? 2 : 3)}
        gap={20}
        width="auto"
        className={clsx({ 'centralize-items': carouselBanners.length < 3 && !showFixedBanner })}
      >
        {carouselBanners.map((banner) => <CruisePromoTileItem
          key={banner.id}
          promoBanner={banner}
          onClick={clickEventHandler}
        />)}
      </PromoTilesCarousel>
    </CruisePromoTilesWrapper>
  </ImpressionTrackerContext.Provider>
}

const mapStateToProps = (state: App.State) => {
  return { cruisePromoBanners: state.cruise.cruisePromoBanners }
}

export default connect(mapStateToProps)(CruisePromoTiles)
